import React from 'react';

import { Box, Card, Divider, Group, Stack, Text, useMantineTheme } from '@mantine/core';
import LevelPicker from 'components/LevelPicker/LevelPicker';
import { sectionHasLevels } from 'components/Workout/logging/levelUtils';
import { MovementResult, ResultsState, WorkoutSectionWithScoring } from 'components/Workout/logging/types';
import ReadonlyWorkoutNotesRTE from 'components/workout-program/ReadonlyWorkoutNotesRTE';
import { WorkoutLevelEnum } from 'types/enums';

import WorkoutMovementInput from './WorkoutMovementInput';

interface WorkoutSectionCardProps {
  section: WorkoutSectionWithScoring;
  sectionIndex: number;
  results: ResultsState;
  level: WorkoutLevelEnum | null;
  onResultChange: (movementId: string, result: MovementResult) => void;
  onResultCommit: (movementId: string, result: MovementResult) => Promise<void>;
  onLevelChange: (level: WorkoutLevelEnum) => void;
  onLevelCommit: (level: WorkoutLevelEnum) => Promise<void>;
}

const WorkoutSectionCard = ({
  section,
  sectionIndex,
  results,
  level,
  onResultChange,
  onResultCommit,
  onLevelChange,
  onLevelCommit,
}: WorkoutSectionCardProps): JSX.Element => {
  const theme = useMantineTheme();
  // 'A', 'B', 'C', ... — charCode 65 is 'A'.
  const sectionLetter = String.fromCharCode(65 + sectionIndex);

  // Levels commit immediately on tap — optimistic update, reverted if the
  // bulk-set request fails. Mirrors the Whiteboard's WhiteboardCell.
  const handleLevelPick = (nextLevel: WorkoutLevelEnum): void => {
    const previous = level;
    onLevelChange(nextLevel);
    onLevelCommit(nextLevel).catch(() => {
      if (previous !== null) onLevelChange(previous);
    });
  };

  return (
    <Stack spacing='sm'>
      <Group spacing='xs' align='center'>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            backgroundColor: theme.colors.blue[6],
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: theme.fontSizes.sm,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {sectionLetter}
        </Box>
        <Text weight={600} size='sm'>
          {section.name}
        </Text>
      </Group>

      <Card withBorder radius='md' p='md'>
        <Stack spacing='xl'>
          <>
            {sectionHasLevels(section) && (
              <LevelPicker
                value={level}
                onChange={handleLevelPick}
                levelScaled={section.levelScaled}
                levelRx={section.levelRx}
                levelRxPlus={section.levelRxPlus}
              />
            )}
            <Stack spacing={4}>
              <Text size='xs' weight={700} color='dimmed' transform='uppercase' sx={{ letterSpacing: 1 }}>
                Workout Description
              </Text>
              {section.workoutInstructions?.trim() ? (
                <Box my='xs'>
                  <ReadonlyWorkoutNotesRTE
                    workoutNotes={section.workoutInstructions}
                    mih={0}
                    bordered={false}
                  />
                </Box>
              ) : (
                <Text size='sm' my='xs' italic color='dimmed'>
                  No section description
                </Text>
              )}
            </Stack>
            <Divider />
          </>

          {section.movements.map((movement, movementIndex) => (
            <WorkoutMovementInput
              key={movement.workoutSectionMovementId}
              movement={movement}
              value={results[movement.workoutSectionMovementId]}
              onChange={onResultChange}
              onCommit={onResultCommit}
              showDivider={movementIndex > 0}
            />
          ))}
        </Stack>
      </Card>
    </Stack>
  );
};

export default WorkoutSectionCard;
